import React, { useCallback } from 'react';
import cogoToast from 'cogo-toast';
import PropTypes from 'prop-types';

import { ACADEMY_MODULE_TYPES } from 'data/academyModuleTypes';
import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import { useLazySearchAcademyModuleQuery } from 'services/academyModuleService';
import Select from 'components/Select';
import useDebouncedPromise from 'hooks/useDebouncedPromise';

const MIN_KEYWORD_LENGTH = 3;

export default function AcademyModuleAsyncSelect({
  category,
  ...remainingProps
}) {
  const [fetchAcademyModules, { isFetching }] =
    useLazySearchAcademyModuleQuery();

  const generateOptions = useCallback(
    (academyModules) =>
      academyModules.map((o) => ({
        value: o.id,
        label: o.name,
        category: o.category,
      })),
    []
  );

  const loadOptions = useDebouncedPromise(
    async (keyword, callback) => {
      if (keyword.length < MIN_KEYWORD_LENGTH) return callback([]);

      const response = await fetchAcademyModules({ category, keyword }, true);
      let options = [];
      if (response.data) {
        options = generateOptions(response.data.data);
      } else {
        cogoToast.error(
          response.error?.data?.message ||
            'Failed to load academy modules! Try again after sometime'
        );
      }
      callback(options);
    },
    DEBOUNCE_DURATION,
    [fetchAcademyModules, generateOptions, category]
  );

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (inputValue.length < MIN_KEYWORD_LENGTH) {
        return `Type atleast ${MIN_KEYWORD_LENGTH} characters to search`;
      } else if (isFetching) {
        return `Searching...`;
      } else {
        return `No matching academy modules found.`;
      }
    },
    [isFetching]
  );

  return (
    <Select
      type="async"
      placeholder="Select Academy Module"
      loadOptions={loadOptions}
      noOptionsMessage={noOptionsMessage}
      {...remainingProps}
    />
  );
}

AcademyModuleAsyncSelect.propTypes = {
  category: PropTypes.oneOf(Object.values(ACADEMY_MODULE_TYPES)),
};
